'use client'

import type { NotificationView } from '@package/notification'
import { renderNotificationCopy } from '@package/notification'
import { Avatar, AvatarFallback, AvatarImage } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { AvatarStack } from './avatar-stack'
import { NotificationCard } from './notification-card'

interface ActorNotificationCardProps {
  notification: NotificationView
  onSelect?: () => void
}

/**
 * The sender's own card. Shows read progress ("N dari M sudah membuka"), which
 * updates as recipients open the share. Never contributes to the badge — see
 * decision 8: the badge counts RECIPIENT rows only, enforced by the router, not here.
 */
export function ActorNotificationCard({ notification, onSelect }: ActorNotificationCardProps) {
  const { snapshot, recipientCount } = notification
  const { progressLine } = renderNotificationCopy(notification)

  /**
   * A share's actor card is about the people who received it, so it leads with their faces and the
   * sender is "kamu" in the copy. Every other actor event is a receipt for something the sender did
   * — the sender is the subject, and there may be no recipient at all (a Sambung Sekolah-Rumah
   * request goes to an email address that need not have an account). Leading those with a recipient
   * stack renders an empty "?" avatar.
   */
  const showsRecipients = notification.category === 'SHARE'

  return (
    <NotificationCard
      notification={notification}
      layout={snapshot.thumbnailUrl ? 'media-below' : 'text-only'}
      onSelect={onSelect}
      leading={
        showsRecipients ? (
          <AvatarStack
            avatars={[{ name: snapshot.recipientName ?? '?', src: snapshot.recipientAvatarUrl }]}
            total={recipientCount}
          />
        ) : (
          <Avatar size='sm'>
            <AvatarImage src={snapshot.actorAvatarUrl} alt='' />
            <AvatarFallback>{(snapshot.actorName ?? '?').charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
        )
      }
    >
      {progressLine && (
        <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-blue-70')}>{progressLine}</p>
      )}
    </NotificationCard>
  )
}
